import React from "react"
import { MuiCard } from "@ace/ace-ui-web"
import { Typography, Stack, Box } from "@mui/material"

interface StatCardProps {
    value: number,
    isPercentage?: boolean,
    title: string,
    note?: string,
    IconProp?: React.ReactNode,
    colorProp?: string,
    variant?: string,
    percentage?: number,
    onClick?: () => void
}

const cardColors = {
    redText: "#d32f2f",
    redBackground: "#ffebee ",
    yellowText: "#fbc02d",
    yellowBackground: "#fffde7",
    greenText: "#388e3c",
    greenBackground: "#e8f5e9",
    lightblueText: "#3333ff",
    lightblueBackground: "#e5f2ff",
    blueText: "#6655ff",
    blueBackground: "#d4dbff",
    purpleText: "#7b1fa2",
    purpleBackground: "#f3e5f5",
    blackText: "#000000",
    blackBackground: "#e0e0e0",
}

const StatCard: React.FC<StatCardProps> = ({
    value,
    isPercentage,
    title,
    note,
    IconProp,
    colorProp,
    variant,
    percentage,
    onClick
}) => {
    const textColor = colorProp ? cardColors[`${colorProp}Text` as keyof typeof cardColors] : undefined;

    return (
        <Box sx={{ 
            display: 'flex', 
            width: '100%', 
            height: '100%',
            cursor: onClick ? 'pointer' : 'default',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease'
        }} onClick={onClick}>
            {variant === "shifttype" && (
                <MuiCard
                    borderRadius={2}
                    elevation={5}
                    padding={0}
                    variant="outlined"
                    sx={{ 
                        width: '100%', 
                        height: '100%', 
                        outline: '1px solid', 
                        outlineColor: textColor,
                        transition: 'all 0.3s ease',
                        ...(onClick && {
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 12px 24px rgba(0, 0, 0, 0.15)'
                            }
                        })
                    }}
                >
                    <Stack direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            {IconProp && <Box sx={{ color: textColor }}>{IconProp}</Box>}
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>{title}</Typography>
                                <Typography variant="body2" sx={{ color: '#888', fontSize: '0.875rem' }}>{note}</Typography>
                            </Box>
                        </Stack>
                        <Stack alignItems="flex-end" justifyContent="center">
                            <Typography variant="h4" sx={{ color: textColor }}>{value}</Typography>
                            {percentage !== undefined && <Typography variant="body2" sx={{ color: '#888', fontSize: '0.875rem' }}>{percentage + "%"}</Typography>}
                        </Stack>
                    </Stack>
                </MuiCard>
            )}
            {variant === "fillrate" && (
                <MuiCard
                    borderRadius={2}
                    elevation={5}
                    padding={0}
                    variant="outlined"
                    sx={{ 
                        width: '100%', 
                        height: '100%', 
                        outline: '1px solid', 
                        outlineColor: textColor,
                        transition: 'all 0.3s ease',
                        ...(onClick && {
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 12px 24px rgba(0, 0, 0, 0.15)'
                            }
                        })
                    }}
                >
                    <Stack direction="row" justifyContent="space-between" sx={{ p: 2, width: '100%' }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            {IconProp && <Box sx={{ color: textColor }}>{IconProp}</Box>}
                            <Stack>
                                <Typography variant="subtitle1" sx={{ color: '#888', fontSize: '0.875rem' }}>{title}</Typography>
                                <Typography variant="h5" sx={{ color: textColor }}>{value}</Typography>
                            </Stack>
                        </Stack>
                        <Stack alignItems="flex-end" justifyContent="center">
                            <Typography variant="body2" sx={{ color: '#888', fontSize: '0.875rem' }}>{note}</Typography>
                            <Typography variant="h4" sx={{ color: textColor }}>{percentage + "%"}</Typography>
                        </Stack>
                    </Stack>
                </MuiCard>
            )}
            {(variant !== "shifttype" && variant !== "fillrate") && (
                <MuiCard
                    borderRadius={2}
                    elevation={5}
                    padding={0}
                    variant="elevation"
                    sx={{ 
                        width: '100%', 
                        height: '100%',
                        transition: 'all 0.3s ease',
                        ...(onClick && {
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 12px 24px rgba(0, 0, 0, 0.15)'
                            }
                        })
                    }}
                >
                    <Stack sx={{ p: '1rem 0.5rem', width: '100%' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>{title}</Typography>
                            {IconProp && <Box sx={{ color: textColor }}>{IconProp}</Box>}
                        </Stack>
                        <Typography variant="h4" sx={{ mt: '1.5rem', color: textColor }}>
                            {isPercentage ? `${value}%` : value}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#888', fontSize: '0.875rem' }}>{note}</Typography>
                    </Stack>
                </MuiCard>
            )}
        </Box>
    );
}

export default StatCard;